import {
  Check,
  Copy,
  Globe,
  LinkIcon,
  Mail,
  MapPin,
  Phone,
  Printer,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Tooltip } from '@/components/ui/v3/Tooltip';
import { useProviderDirectory } from '@/hooks/useProviderDirectory';
import {
  ContactHandle,
  ContactHandleType,
} from '@/services/types/treatment-types';

interface ContactCardProps {
  handles?: ContactHandle[];
  providerContactRefId?: string | null;
  className?: string;
}

const getIcon = (type: ContactHandleType) => {
  switch (type) {
    case ContactHandleType.PHONE:
      return <Phone className="h-3.5 w-3.5 text-blue-600" />;
    case ContactHandleType.EMAIL:
      return <Mail className="h-3.5 w-3.5 text-blue-600" />;
    case ContactHandleType.FAX:
      return <Printer className="h-3.5 w-3.5 text-blue-600" />;
    case ContactHandleType.WEBSITE:
      return <Globe className="h-3.5 w-3.5 text-blue-600" />;
    case ContactHandleType.MAILING_ADDRESS:
      return <MapPin className="h-3.5 w-3.5 text-blue-600" />;
    default:
      return <Phone className="h-3.5 w-3.5 text-blue-600" />;
  }
};

const getLabel = (type: ContactHandleType): string => {
  switch (type) {
    case ContactHandleType.PHONE:
      return 'Phone';
    case ContactHandleType.EMAIL:
      return 'Email';
    case ContactHandleType.FAX:
      return 'Fax';
    case ContactHandleType.WEBSITE:
      return 'Website';
    case ContactHandleType.MAILING_ADDRESS:
      return 'Address';
    case ContactHandleType.OTHER:
      return 'Other';
    default:
      return 'Contact';
  }
};

interface CopyButtonProps {
  value: string;
  label: string;
}

const CopyButton = ({ value, label }: CopyButtonProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`Copied ${label}`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
      title={`Copy ${label}`}
    >
      {copied ? (
        <Check className="h-3.5 w-3.5 text-green-600" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-gray-400" />
      )}
    </button>
  );
};

const ContactItem = ({
  handle,
  isPrimary,
  isFromDirectory,
}: {
  handle: ContactHandle;
  isPrimary?: boolean;
  isFromDirectory?: boolean;
}) => {
  const icon = getIcon(handle.handle_type);
  const label = getLabel(handle.handle_type);

  return (
    <div className="group flex items-start gap-2 py-1.5 hover:bg-gray-50 px-2 -mx-2 rounded transition-colors">
      <div className="flex-shrink-0 mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-gray-700">
            {handle.name || label}
          </span>
          {isPrimary && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
              PRIMARY
            </span>
          )}
          {isFromDirectory && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 bg-green-50 text-green-700 rounded flex items-center gap-0.5">
              <LinkIcon className="h-2.5 w-2.5" />
              Directory
            </span>
          )}
        </div>
        <div className="text-sm text-gray-900 font-mono break-all">
          {handle.value}
        </div>
      </div>
      <CopyButton value={handle.value} label={handle.name || label} />
    </div>
  );
};

export const ContactCard = ({
  handles,
  providerContactRefId,
  className = '',
}: ContactCardProps) => {
  // Fetch directory provider handles if linked
  const { handlesByUnit, isLoading } = useProviderDirectory({
    providerRefId: providerContactRefId,
    skip: !providerContactRefId,
  });

  // Merge handles from provider and directory
  const mergedHandles = useMemo<
    Array<ContactHandle & { isFromDirectory?: boolean; isPrimary?: boolean }>
  >(() => {
    const allHandles: Array<
      ContactHandle & { isFromDirectory?: boolean; isPrimary?: boolean }
    > = [...(handles || []).map((h) => ({ ...h, isFromDirectory: false }))];

    // Add directory handles
    if (providerContactRefId && handlesByUnit) {
      Object.values(handlesByUnit).forEach((unitHandles) => {
        unitHandles.forEach((dirHandle) => {
          allHandles.push({
            value: dirHandle.handle.value,
            handle_type: dirHandle.handle.handle_type as ContactHandleType,
            name: dirHandle.handle.name || undefined,
            isFromDirectory: true,
            isPrimary: dirHandle.is_preferred,
          });
        });
      });
    }

    return allHandles;
  }, [handles, handlesByUnit, providerContactRefId]);

  if (isLoading) {
    return (
      <div className={`text-xs text-gray-400 italic ${className}`}>
        Loading contacts...
      </div>
    );
  }

  if (mergedHandles.length === 0) {
    return (
      <div className={`text-xs text-gray-400 italic ${className}`}>
        No contacts available
      </div>
    );
  }

  // Group handles by type for better organization
  const phoneHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.PHONE,
  );
  const faxHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.FAX,
  );
  const emailHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.EMAIL,
  );
  const addressHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.MAILING_ADDRESS,
  );
  const otherHandles = mergedHandles.filter(
    (h) =>
      h.handle_type !== ContactHandleType.PHONE &&
      h.handle_type !== ContactHandleType.FAX &&
      h.handle_type !== ContactHandleType.EMAIL &&
      h.handle_type !== ContactHandleType.MAILING_ADDRESS,
  );

  return (
    <div
      className={`border border-gray-200 rounded-lg bg-white divide-y divide-gray-100 ${className}`}
    >
      {/* Phone Numbers */}
      {phoneHandles.length > 0 && (
        <div className="p-3 space-y-1">
          {phoneHandles.map((handle, index) => (
            <ContactItem
              key={`phone-${index}`}
              handle={handle}
              isPrimary={index === 0}
              isFromDirectory={handle.isFromDirectory}
            />
          ))}
        </div>
      )}

      {/* Fax Numbers */}
      {faxHandles.length > 0 && (
        <div className="p-3 space-y-1">
          {faxHandles.map((handle, index) => (
            <ContactItem
              key={`fax-${index}`}
              handle={handle}
              isPrimary={index === 0}
              isFromDirectory={handle.isFromDirectory}
            />
          ))}
        </div>
      )}

      {/* Email Addresses */}
      {emailHandles.length > 0 && (
        <div className="p-3 space-y-1">
          {emailHandles.map((handle, index) => (
            <ContactItem
              key={`email-${index}`}
              handle={handle}
              isPrimary={index === 0}
              isFromDirectory={handle.isFromDirectory}
            />
          ))}
        </div>
      )}

      {/* Addresses */}
      {addressHandles.length > 0 && (
        <div className="p-3 space-y-1">
          {addressHandles.map((handle, index) => (
            <ContactItem
              key={`address-${index}`}
              handle={handle}
              isFromDirectory={handle.isFromDirectory}
            />
          ))}
        </div>
      )}

      {/* Other Contacts */}
      {otherHandles.length > 0 && (
        <div className="p-3 space-y-1">
          {otherHandles.map((handle, index) => (
            <ContactItem
              key={`other-${index}`}
              handle={handle}
              isFromDirectory={handle.isFromDirectory}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ContactHandleButton = ({
  handle,
  icon,
  label,
  isPrimary,
}: {
  handle: ContactHandle;
  icon: React.ReactNode;
  label: string;
  isPrimary?: boolean;
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(handle.value);
      setCopied(true);
      toast.success(`Copied ${handle.name || label}`);
      setTimeout(() => setCopied(false), 1000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="flex items-center gap-1.5 text-xs flex-wrap">
      {icon}
      <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
        {label}:
      </span>
      <Tooltip content="Click to copy" variant="dark" side="top">
        <button
          type="button"
          onClick={handleCopy}
          className={`text-xs font-mono break-all cursor-pointer transition-colors underline decoration-dotted underline-offset-2 text-left ${
            copied
              ? 'text-green-600 font-semibold'
              : 'text-gray-700 hover:text-blue-600'
          }`}
        >
          {handle.value}
        </button>
      </Tooltip>
      {isPrimary && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">
          Primary
        </span>
      )}
      {handle.name && (
        <span className="text-[10px] font-medium px-1.5 py-0.5 bg-gray-100 text-gray-700 rounded">
          {handle.name}
        </span>
      )}
    </div>
  );
};

/**
 * Compact inline version for summary views
 */
export const ContactCardCompact = ({
  handles,
  providerContactRefId,
  className = '',
}: ContactCardProps) => {
  // Fetch directory provider handles if linked
  const { handlesByUnit, isLoading } = useProviderDirectory({
    providerRefId: providerContactRefId,
    skip: !providerContactRefId,
  });

  // Merge handles from provider and directory
  const mergedHandles = useMemo<
    Array<ContactHandle & { isFromDirectory?: boolean; isPrimary?: boolean }>
  >(() => {
    const allHandles: Array<
      ContactHandle & { isFromDirectory?: boolean; isPrimary?: boolean }
    > = [...(handles || []).map((h) => ({ ...h, isFromDirectory: false }))];

    // Add directory handles
    if (providerContactRefId && handlesByUnit) {
      Object.values(handlesByUnit).forEach((unitHandles) => {
        unitHandles.forEach((dirHandle) => {
          allHandles.push({
            value: dirHandle.handle.value,
            handle_type: dirHandle.handle.handle_type as ContactHandleType,
            name: dirHandle.handle.name || undefined,
            isFromDirectory: true,
            isPrimary: dirHandle.is_preferred,
          });
        });
      });
    }

    return allHandles;
  }, [handles, handlesByUnit, providerContactRefId]);

  if (isLoading) {
    return null;
  }

  // Group handles by type
  const phoneHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.PHONE,
  );
  const emailHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.EMAIL,
  );
  const faxHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.FAX,
  );
  const websiteHandles = mergedHandles.filter(
    (h) => h.handle_type === ContactHandleType.WEBSITE,
  );

  return (
    <div className={`flex flex-col gap-1.5 ${className}`}>
      {phoneHandles.length > 0 ? (
        phoneHandles.map((handle, index) => (
          <ContactHandleButton
            key={`phone-${index}`}
            handle={handle}
            icon={<Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
            label="Phone"
            isPrimary={handle.isPrimary}
          />
        ))
      ) : (
        <div className="flex items-center gap-1.5 text-xs">
          <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
            Phone:
          </span>
          <span className="text-xs text-gray-500">Not provided</span>
        </div>
      )}
      {faxHandles.length > 0 ? (
        faxHandles.map((handle, index) => (
          <ContactHandleButton
            key={`fax-${index}`}
            handle={handle}
            icon={
              <Printer className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
            }
            label="Fax"
            isPrimary={handle.isPrimary}
          />
        ))
      ) : (
        <div className="flex items-center gap-1.5 text-xs">
          <Printer className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
            Fax:
          </span>
          <span className="text-xs text-gray-500">Not provided</span>
        </div>
      )}
      {emailHandles.length > 0 ? (
        emailHandles.map((handle, index) => (
          <ContactHandleButton
            key={`email-${index}`}
            handle={handle}
            icon={<Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
            label="Email"
            isPrimary={handle.isPrimary}
          />
        ))
      ) : (
        <div className="flex items-center gap-1.5 text-xs">
          <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
            Email:
          </span>
          <span className="text-xs text-gray-500">Not provided</span>
        </div>
      )}
      {websiteHandles.length > 0 ? (
        websiteHandles.map((handle, index) => (
          <ContactHandleButton
            key={`website-${index}`}
            handle={handle}
            icon={<Globe className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
            label="Website"
            isPrimary={handle.isPrimary}
          />
        ))
      ) : (
        <div className="flex items-center gap-1.5 text-xs">
          <Globe className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
          <span className="text-xs font-semibold text-gray-500 flex-shrink-0">
            Website:
          </span>
          <span className="text-xs text-gray-500">Not provided</span>
        </div>
      )}
    </div>
  );
};
